import { useCallback } from "react";
import { toast } from "sonner";
import { isElectron } from "@/lib/platform";
import type { ScreenSource } from "@/types/electron";

export type ElectronScreenShare = {
	isElectron: boolean;
	getScreenSources: () => Promise<ScreenSource[]>;
	createScreenShareStream: (sourceId: string) => Promise<MediaStream | null>;
};

export function useElectronScreenShare(): ElectronScreenShare {
	const getScreenSources = useCallback(async (): Promise<ScreenSource[]> => {
		if (!isElectron) return [];
		if (!window.api?.media) return [];

		const status = await window.api.media.checkPermission("screen");
		if (status === "denied" || status === "restricted") {
			toast.error(
				"Screen recording access blocked. Please enable screen recording in System Preferences > Security & Privacy > Privacy > Screen Recording.",
				{ duration: 6000 }
			);
			return [];
		}

		return window.api.media.getScreenSources();
	}, []);

	const createScreenShareStream = useCallback(
		async (sourceId: string): Promise<MediaStream | null> => {
			if (!isElectron) return null;

			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					audio: false,
					video: {
						// @ts-expect-error - Electron-specific constraint
						mandatory: {
							chromeMediaSource: "desktop",
							chromeMediaSourceId: sourceId,
						},
					},
				});
				return stream;
			} catch (error) {
				console.error("Failed to create screen share stream:", error);
				toast.error("Failed to start screen sharing");
				return null;
			}
		},
		[]
	);

	return {
		isElectron,
		getScreenSources,
		createScreenShareStream,
	};
}
